
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import { getBlocksAndPutIntoStore } from "@/lib/global-blocks/global-blocks";
import { configStore, uiStore } from "@/store/store";
import { getDocumentUrlFromId } from "@/lib/wordpress/load";
import { breakdanceUndo } from "@/lib/undo/undo";
import NiceButton from "@/components/UI/NiceButton.vue";

@Component({
  components: {
    NiceButton,
    Icon
  }
})
export default class GlobalBlockInput extends Vue {
  @Prop()
  value: number | undefined;

  loading = false;

  get allBlocks() {
    return configStore.globalBlocks;
  }

  get selectedBlock() {
    return this.allBlocks.find(block => block.id === this.value);
  }

  get items() {
    return this.allBlocks.map(globalBlock => {
      return {
        text: `${globalBlock.label} ${
          globalBlock.tree === false ? "(Empty)" : ""
        }`,
        value: globalBlock.id
      };
    });
  }

  get element() {
    return uiStore.activeElement;
  }

  get baseElement() {
    if (!this.element) return null;
    return configStore.element(this.element.data.type);
  }

  get blockDocumentHref() {
    if (!this.value) return false;
    return getDocumentUrlFromId(this.value);
  }

  get context() {
    return uiStore.inlineEditingContext;
  }

  get isEditDisabled() {
    if (this.loading && !this.selectedBlock) return true;
    return !this.blockDocumentHref;
  }

  get isEditingInline() {
    return (
      this.context?.blockId === this.value &&
      this.context?.elementId === uiStore.activeElementId
    );
  }

  get canEditInline() {
    // A block id can be set, but the block might be deleted.
    if (!this.selectedBlock) return false;

    // Disable inline editing for nested blocks. Instead, redirect to another page.
    if (this.context?.blockId && this.context?.blockId !== this.value) {
      return false;
    }

    return !!this.baseElement?.nestingRule.inlineEditable;
  }

  async loadBlocks() {
    await getBlocksAndPutIntoStore();

    this.loading = false;
  }

  edit(string: unknown) {
    uiStore.resetInlineEditingContext();

    if (!string) {
      // eslint-disable-next-line no-param-reassign
      string = "";
    }

    this.$emit("input", string);
  }

  toggleInlineEdit(event: MouseEvent) {
    if (this.isEditingInline) {
      event.preventDefault();
      uiStore.resetInlineEditingContext();
    } else if (this.canEditInline) {
      if (event.ctrlKey || event.metaKey) return;
      event.preventDefault();

      breakdanceUndo.transaction(
        () => {
          uiStore.setInlineEditingContext({
            blockId: this.value,
            elementId: uiStore.activeElementId!
          });
        },
        "Start Inline Editing",
        `inline-editing:${uiStore.activeElementId!}`
      );
    }

    // If the block is not inline editable, you are redirected to edit the block on a separate page.
  }

  onVisibilityChange() {
    if (document.hidden) return;
    this.loadBlocks();
  }

  mounted() {
    this.loading = true;
    this.loadBlocks();

    document.addEventListener(
      "visibilitychange",
      this.onVisibilityChange,
      false
    );
  }

  beforeDestroy() {
    document.removeEventListener(
      "visibilitychange",
      this.onVisibilityChange,
      false
    );
  }
}
